import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { GitBranch, Plus, Trash2, Loader2, FlaskConical, Users, Globe, Building2, ChevronDown, Check } from 'lucide-react';
import { useChannel } from '../../contexts/ChannelContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  off: { label: 'Off', desc: 'Désactivé partout', cls: 'bg-gray-200 text-gray-700' },
  internal: { label: 'Interne', desc: 'Lab + Prod interne uniquement', cls: 'bg-blue-500 text-white' },
  stable: { label: 'Stable', desc: 'Visible par les clients', cls: 'bg-emerald-500 text-white' },
};

const CHANNEL_META = {
  lab: { label: 'LAB', icon: FlaskConical, cls: 'bg-orange-500/15 text-orange-600 border-orange-400' },
  internal: { label: 'PROD INTERNE', icon: Users, cls: 'bg-blue-500/15 text-blue-600 border-blue-400' },
  stable: { label: 'STABLE (CLIENTS)', icon: Globe, cls: 'bg-emerald-500/15 text-emerald-600 border-emerald-400' },
};

const StatusSegment = ({ value, onChange, testid }) => (
  <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden" data-testid={testid}>
    {Object.entries(STATUS_META).map(([k, m]) => (
      <button
        key={k}
        type="button"
        onClick={() => onChange(k)}
        title={m.desc}
        className={`px-3 h-8 text-xs font-semibold transition-colors ${
          value === k ? m.cls : 'bg-white text-gray-400 hover:text-gray-700'
        }`}
        data-testid={`${testid}-${k}`}
      >
        {m.label}
      </button>
    ))}
  </div>
);

const FeatureFlagsTab = () => {
  const { channel } = useChannel();
  const [flags, setFlags] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [expandedOrgsFor, setExpandedOrgsFor] = useState(null);
  const [saving, setSaving] = useState(null);
  const [newFlag, setNewFlag] = useState({ key: '', label: '' });
  const [creating, setCreating] = useState(false);

  const load = () => {
    axios.get(`${API}/admin/feature-flags`, { withCredentials: true })
      .then(r => setFlags(r.data))
      .catch(() => toast.error('Impossible de charger les flags'));
    axios.get(`${API}/admin/organizations`, { withCredentials: true })
      .then(r => setOrgs(r.data || []))
      .catch(() => {});
  };
  useEffect(load, []);

  const setStatus = async (key, status) => {
    setSaving(key);
    try {
      await axios.put(`${API}/admin/feature-flags/${key}`, { status }, { withCredentials: true });
      setFlags(fs => fs.map(f => f.key === key ? { ...f, status } : f));
      toast.success(`"${key}" → ${STATUS_META[status].label}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de la mise à jour');
    } finally { setSaving(null); }
  };

  const createFlag = async () => {
    if (!newFlag.key.trim() || !newFlag.label.trim()) return;
    setCreating(true);
    try {
      await axios.post(`${API}/admin/feature-flags`, {
        key: newFlag.key.trim(), label: newFlag.label.trim(), status: 'internal',
      }, { withCredentials: true });
      setNewFlag({ key: '', label: '' });
      load();
      toast.success('Flag créé (statut Interne par défaut)');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de la création');
    } finally { setCreating(false); }
  };

  const toggleOrgForFlag = async (flag, orgId) => {
    const current = flag.allowed_org_ids || [];
    const next = current.includes(orgId)
      ? current.filter(id => id !== orgId)
      : [...current, orgId];
    setSaving(flag.key);
    try {
      await axios.put(`${API}/admin/feature-flags/${flag.key}`, { allowed_org_ids: next }, { withCredentials: true });
      setFlags(fs => fs.map(f => f.key === flag.key ? { ...f, allowed_org_ids: next } : f));
    } catch {
      toast.error('Échec de la mise à jour des orgs beta');
    } finally { setSaving(null); }
  };

  const deleteFlag = async (key) => {
    try {
      await axios.delete(`${API}/admin/feature-flags/${key}`, { withCredentials: true });
      setFlags(fs => fs.filter(f => f.key !== key));
      toast.success('Flag supprimé');
    } catch {
      toast.error('Échec de la suppression');
    }
  };

  const ch = CHANNEL_META[channel] || CHANNEL_META.stable;
  const ChIcon = ch.icon;

  return (
    <div className="space-y-6" data-testid="feature-flags-tab">
      {/* Bandeau canal courant */}
      <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${ch.cls}`} data-testid="current-channel-banner">
        <ChIcon className="w-5 h-5" />
        <div>
          <p className="text-sm font-bold">Canal de cet environnement : {ch.label}</p>
          <p className="text-xs opacity-80">
            Variable <code className="font-mono">APP_CHANNEL</code> du service — lab (branche Git) · internal (pro.peters-universe.fr) · stable (peters-universe.fr). Défaut fail-safe : stable.
          </p>
        </div>
      </div>

      {/* Légende */}
      <div className="flex items-center gap-4 text-xs text-gray-500 flex-wrap">
        {Object.entries(STATUS_META).map(([k, m]) => (
          <span key={k} className="inline-flex items-center gap-1.5">
            <span className={`w-2.5 h-2.5 rounded-full ${m.cls.split(' ')[0]}`} />
            <b>{m.label}</b> — {m.desc}
          </span>
        ))}
      </div>

      {/* Liste */}
      {!flags ? (
        <div className="flex items-center gap-2 text-gray-400 text-sm py-8 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
        </div>
      ) : (
        <div className="border border-gray-200 rounded-xl overflow-hidden divide-y divide-gray-100 bg-white">
          {flags.map(f => (
            <div key={f.key} data-testid={`flag-row-${f.key}`}>
              <div className="flex items-center gap-4 px-4 py-3">
                <GitBranch className="w-4 h-4 text-gray-300 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900">{f.label}</p>
                  <p className="text-[11px] text-gray-400 truncate">
                    <code className="font-mono">{f.key}</code>{f.description ? ` — ${f.description}` : ''}
                  </p>
                </div>
                {saving === f.key && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
                {f.status === 'internal' && (
                  <button
                    type="button"
                    onClick={() => setExpandedOrgsFor(v => v === f.key ? null : f.key)}
                    title="Ouvrir cette beta à des organisations partenaires (même sur canal stable)"
                    className={`inline-flex items-center gap-1 px-2 h-8 rounded-lg border text-[11px] font-semibold transition-colors ${
                      (f.allowed_org_ids || []).length > 0
                        ? 'border-blue-300 text-blue-600 bg-blue-50'
                        : 'border-gray-200 text-gray-400 hover:text-gray-700'
                    }`}
                    data-testid={`flag-orgs-btn-${f.key}`}
                  >
                    <Building2 className="w-3.5 h-3.5" />
                    {(f.allowed_org_ids || []).length || 'Beta orgs'}
                    <ChevronDown className={`w-3 h-3 transition-transform ${expandedOrgsFor === f.key ? 'rotate-180' : ''}`} />
                  </button>
                )}
                <StatusSegment value={f.status} onChange={(s) => setStatus(f.key, s)} testid={`flag-status-${f.key}`} />
                <button
                  onClick={() => deleteFlag(f.key)}
                  className="text-gray-300 hover:text-red-500 transition-colors"
                  title="Supprimer le flag"
                  data-testid={`flag-delete-${f.key}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              {expandedOrgsFor === f.key && f.status === 'internal' && (
                <div className="px-12 pb-3" data-testid={`flag-orgs-panel-${f.key}`}>
                  <p className="text-[11px] text-gray-400 mb-2">
                    Organisations partenaires ayant accès à cette beta <b>même sur le canal stable</b> :
                  </p>
                  {orgs.length === 0 ? (
                    <p className="text-xs text-gray-400">Aucune organisation.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {orgs.map(o => {
                        const active = (f.allowed_org_ids || []).includes(o.organization_id);
                        return (
                          <button
                            key={o.organization_id}
                            type="button"
                            onClick={() => toggleOrgForFlag(f, o.organization_id)}
                            className={`inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full border text-[11px] font-medium transition-colors ${
                              active
                                ? 'bg-blue-500 text-white border-blue-500'
                                : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'
                            }`}
                            data-testid={`flag-org-chip-${f.key}-${o.organization_id}`}
                          >
                            {active && <Check className="w-3 h-3" />}
                            {o.name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          {flags.length === 0 && (
            <p className="text-center text-sm text-gray-400 py-8">Aucun flag — créez-en un ci-dessous.</p>
          )}
        </div>
      )}

      {/* Création */}
      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <label className="block text-[11px] font-bold text-gray-500 mb-1">Clé (snake_case)</label>
          <Input
            value={newFlag.key}
            onChange={(e) => setNewFlag(v => ({ ...v, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') }))}
            placeholder="mon_nouveau_module"
            className="h-9 w-56 font-mono text-xs"
            data-testid="new-flag-key-input"
          />
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="block text-[11px] font-bold text-gray-500 mb-1">Libellé</label>
          <Input
            value={newFlag.label}
            onChange={(e) => setNewFlag(v => ({ ...v, label: e.target.value }))}
            placeholder="Mon nouveau module"
            className="h-9"
            data-testid="new-flag-label-input"
          />
        </div>
        <Button
          onClick={createFlag}
          disabled={creating || !newFlag.key.trim() || !newFlag.label.trim()}
          className="h-9"
          data-testid="new-flag-create-btn"
        >
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Créer</>}
        </Button>
      </div>
    </div>
  );
};

export default FeatureFlagsTab;
